import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { bulkResultSchema, bulkRowSchema, sortedListRequest } from './common/list.js';
import { moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

export const RECEIPT_KINDS = ['donation', 'annual_certificate'] as const;
export const RECEIPT_STATUSES = ['issued', 'voided'] as const;
export const RECEIPT_RENDER_STATUSES = ['pending', 'ready', 'failed'] as const;
export const RECEIPT_LEGAL_DOCUMENT_FILTERS = ['recorded', 'missing'] as const;
export const RECEIPT_SORT_FIELDS = ['issuedAt', 'receiptNo'] as const;

/** A bulk issue names at most this many rows, each written in its own transaction. */
export const MAX_RECEIPT_BULK_ITEMS = 500;

const yearSchema = z.coerce.number().int().min(2000).max(2999);

const reasonSchema = z.string().trim().min(1).max(500);

/**
 * A donation acknowledgement or a donor's annual certificate — never the legal
 * tax document, whose number is recorded on it where a jurisdiction requires
 * one — docs/decisions/0046-a-receipt-is-an-acknowledgement-numbered-per-institution.md.
 * `donorName` is null for an anonymous donor, and the PDF prints the anonymous
 * label in its place.
 */
export const receiptSchema = z.object({
  id: z.uuid(),
  kind: z.enum(RECEIPT_KINDS),
  receiptNo: z.string(),
  status: z.enum(RECEIPT_STATUSES),
  year: z.number().int(),
  institutionId: z.uuid(),
  institutionName: z.string(),
  institutionCode: z.string(),
  organizationId: z.uuid(),
  organizationName: z.string(),
  donationId: z.uuid().nullable(),
  donationReferenceCode: z.string().nullable(),
  donorId: z.uuid(),
  donorName: z.string().nullable(),
  donorAnonymous: z.boolean(),
  coversYear: z.number().int().nullable(),
  amount: moneySchema.nullable(),
  itemCount: z.number().int(),
  issuedAt: z.string(),
  voidedAt: z.string().nullable(),
  voidReason: z.string().nullable(),
  replacesReceiptId: z.uuid().nullable(),
  replacesReceiptNo: z.string().nullable(),
  replacedByReceiptId: z.uuid().nullable(),
  legalDocumentSeries: z.string().nullable(),
  legalDocumentNo: z.string().nullable(),
  legalRecordedAt: z.string().nullable(),
  renderStatus: z.enum(RECEIPT_RENDER_STATUSES),
  /**
   * The server's answer to whether the PDF may be asked for again: its render
   * failed, or it has been pending longer than any retry of its job could take.
   */
  renderRetryable: z.boolean(),
  renderedAt: z.string().nullable(),
});

export type Receipt = z.infer<typeof receiptSchema>;

export const receiptListInputSchema = sortedListRequest(
  RECEIPT_SORT_FIELDS,
  'issuedAt',
  'desc',
).extend({
  institutionId: z.uuid().optional(),
  year: yearSchema.optional(),
  kind: z.enum(RECEIPT_KINDS).optional(),
  status: z.enum(RECEIPT_STATUSES).optional(),
  legalDocument: z.enum(RECEIPT_LEGAL_DOCUMENT_FILTERS).optional(),
});

export type ReceiptListInput = z.infer<typeof receiptListInputSchema>;

/**
 * What the donation page may offer, decided on the server: whether a receipt
 * can be issued for a settled donation that never had one, and which voided
 * receipt a reissue would replace while the donation is still settled.
 */
export const donationReceiptOfferSchema = z.object({
  canIssue: z.boolean(),
  reissuableReceiptId: z.uuid().nullable(),
});

export type DonationReceiptOffer = z.infer<typeof donationReceiptOfferSchema>;

export const receiptBulkIssueCodes = [
  'NOT_FOUND',
  'RECEIPT_DONATION_NOT_SETTLED',
  'RECEIPT_DONATION_NO_DONOR',
  'RECEIPT_ALREADY_ISSUED',
  'RECEIPT_REISSUE_REQUIRED',
  'RECEIPT_ORGANIZATION_UNKNOWN',
] as const;

export const receiptBulkIssueResultSchema = bulkRowSchema(receiptBulkIssueCodes).extend({
  receiptId: z.uuid().nullable(),
});

export type ReceiptBulkIssueResult = z.infer<typeof receiptBulkIssueResultSchema>;

const distinctIds = (ids: readonly string[]) => new Set(ids).size === ids.length;

export const issueMissingInputSchema = z.object({
  donationIds: z
    .array(z.uuid())
    .min(1)
    .max(MAX_RECEIPT_BULK_ITEMS)
    .refine(distinctIds, 'a donation appears once in one request'),
});

export const legalDocumentInputSchema = z.object({
  id: z.uuid(),
  series: z.string().trim().min(1).max(32).optional(),
  number: z.string().trim().min(1).max(64),
});

export type LegalDocumentInput = z.infer<typeof legalDocumentInputSchema>;

export const receiptDownloadSchema = z.object({
  url: z.string(),
  fileName: z.string(),
  expiresInSeconds: z.number().int(),
});

export type ReceiptDownload = z.infer<typeof receiptDownloadSchema>;

/**
 * A donor merge group with active donation receipts issued in the year and no
 * active certificate for it. `totals` is one figure per currency, because a
 * group gives in several and nothing here converts.
 */
export const annualCandidateSchema = z.object({
  donorId: z.uuid(),
  donorName: z.string().nullable(),
  donorAnonymous: z.boolean(),
  institutionId: z.uuid(),
  institutionName: z.string(),
  /**
   * The institutions that issued the receipts the row counts — what a reader
   * filters by. `institutionName` is the donor's own institution, whose
   * counter numbers the certificate (docs/decisions/0046).
   */
  receiptInstitutionNames: z.array(z.string()),
  /** The organizations of the receipts the row counts; a certificate is issued for one of them. */
  receiptOrganizations: z.array(z.object({ id: z.uuid(), name: z.string() })),
  receiptCount: z.number().int(),
  totals: z.array(moneySchema),
  lastIssuedAt: z.string(),
});

export type AnnualCandidate = z.infer<typeof annualCandidateSchema>;

export const annualCandidatesInputSchema = paginationInputSchema.extend({
  coversYear: yearSchema,
  institutionId: z.uuid().optional(),
  organizationId: z.uuid().optional(),
});

export type AnnualCandidatesInput = z.infer<typeof annualCandidatesInputSchema>;

export const annualCertificateCodes = [
  'NOT_FOUND',
  'OUT_OF_SCOPE',
  'RECEIPT_CERTIFICATE_EXISTS',
  'RECEIPT_CERTIFICATE_EMPTY',
  'RECEIPT_CERTIFICATE_ORGANIZATION_REQUIRED',
  'RECEIPT_ORGANIZATION_UNKNOWN',
] as const;

export const annualCertificateResultSchema = bulkRowSchema(annualCertificateCodes).extend({
  receiptId: z.uuid().nullable(),
});

export type AnnualCertificateResult = z.infer<typeof annualCertificateResultSchema>;

export const issueAnnualCertificatesInputSchema = z.object({
  coversYear: yearSchema,
  /**
   * The organization the certificates are for. A certificate lists the receipts
   * of one organization (docs/decisions/0112); a donor whose year's receipts
   * span several is refused `RECEIPT_CERTIFICATE_ORGANIZATION_REQUIRED` until
   * one is named.
   */
  organizationId: z.uuid().optional(),
  donorIds: z
    .array(z.uuid())
    .min(1)
    .max(MAX_RECEIPT_BULK_ITEMS)
    .refine(distinctIds, 'a donor appears once in one request'),
});

export type IssueAnnualCertificatesInput = z.infer<typeof issueAnnualCertificatesInputSchema>;

/**
 * Receipts — docs/plans/05-finance-reconciliation.md §5.6. Every read and write
 * is scoped by the receipt's institution; a refusal arrives as `details.reason`
 * under `CONFLICT` or `VALIDATION_FAILED`. A donation's receipt is issued by the
 * transaction that settles it, not by a route here.
 */
export const receiptContract = {
  list: oc
    .route({ method: 'GET', path: '/receipts' })
    .errors(guarded)
    .input(receiptListInputSchema)
    .output(paginated(receiptSchema)),

  /** Read through donation scope, as `donation.get` is. */
  byDonation: oc
    .route({ method: 'GET', path: '/donations/{donationId}/receipts' })
    .errors(guarded)
    .input(z.object({ donationId: z.uuid() }))
    .output(z.object({ items: z.array(receiptSchema), offer: donationReceiptOfferSchema })),

  /** Donations settled before receipts existed, or whose settlement had no receipt; one transaction per row. */
  issueMissing: oc
    .route({ method: 'POST', path: '/receipts/issue-missing' })
    .errors(guarded)
    .input(issueMissingInputSchema)
    .output(bulkResultSchema(receiptBulkIssueResultSchema)),

  /** A receipt is voided with a reason, never deleted or renumbered. */
  void: oc
    .route({ method: 'POST', path: '/receipts/{id}/void' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: reasonSchema }))
    .output(receiptSchema),

  /** A new number naming the voided receipt it replaces; numbers are never reused. */
  reissue: oc
    .route({ method: 'POST', path: '/receipts/{id}/reissue' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(receiptSchema),

  /** A failed render, or one pending past every retry of its job, asked for again. */
  render: oc
    .route({ method: 'POST', path: '/receipts/{id}/render' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(receiptSchema),

  /** The jurisdiction's own legal document number, printed on the next render. */
  recordLegalDocument: oc
    .route({ method: 'POST', path: '/receipts/{id}/legal-document' })
    .errors(guarded)
    .input(legalDocumentInputSchema)
    .output(receiptSchema),

  /** A 60-second presigned link; every call writes an audit row, whether or not the link is used. */
  download: oc
    .route({ method: 'GET', path: '/receipts/{id}/download' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(receiptDownloadSchema),

  annualCandidates: oc
    .route({ method: 'GET', path: '/receipts/annual-candidates' })
    .errors(guarded)
    .input(annualCandidatesInputSchema)
    .output(paginated(annualCandidateSchema)),

  /** One certificate per donor merge group per year; one transaction per donor. */
  issueAnnualCertificates: oc
    .route({ method: 'POST', path: '/receipts/annual-certificates' })
    .errors(guarded)
    .input(issueAnnualCertificatesInputSchema)
    .output(bulkResultSchema(annualCertificateResultSchema)),
};
